import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { Link } from "react-router-dom";
import qs from "qs";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  fa1,
  fa2,
  fa3,
  faA,
  faB,
  faCat,
  faDog,
  faDove,
  faFish,
  faCrow,
  faGraduationCap
} from "@fortawesome/free-solid-svg-icons";

import ButtonLink from "./elements/ButtonLink";
import threeChildrenImg from "./img/HomePage/Tablet/threeChildren.png";

// utils
import {
  useSignout,
  useMyBestUserSignin,
  useSSOAutoLoginState
} from "../utils/authHelpers";
import { openTabletAuthForLogout } from "../utils/authHelpers/useSSOAutoLogin";
import { MYBESTUSER, CLIENTID } from "../config/environment";

// config
import {
  default as ACCESS_LEVELS,
  ALL,
  ADMINS,
  STUDENT
} from "../config/accessLevels";

// test
import { useLanguage } from "../utils/languageHelpers";

const MainBGContent = styled.div`
  position: relative;
  background: linear-gradient(to bottom right, #fef7f0 0%, #fef3e8 100%);
  height: 100vh;
  width: 100vw;
  overflow: hidden;

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 10% 90%;
  grid-template-areas:
    "topNav"
    "content";
`;

const BackgroundBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  opacity: ${(props) => props.$opacity || "0.3"};
  background-color: ${(props) => props.$color || "#dbeafe"};
  width: ${(props) => props.$width || "40vw"};
  height: ${(props) => props.$height || "40vw"};
  top: ${(props) => props.$top || "auto"};
  bottom: ${(props) => props.$bottom || "auto"};
  left: ${(props) => props.$left || "auto"};
  right: ${(props) => props.$right || "auto"};
  filter: blur(${(props) => props.$blur || "100px"});
`;

const FloatingIcon = styled.div`
  position: absolute;
  font-size: ${(props) => props.$size || "4rem"};
  color: ${(props) => props.$color || "#fbbf24"};
  opacity: ${(props) => props.$opacity || "0.15"};
  top: ${(props) => props.$top || "50%"};
  left: ${(props) => props.$left || "50%"};
  transform: rotate(${(props) => props.$rotate || "0deg"});
  pointer-events: none;
  user-select: none;
  animation: ${(props) =>
    props.$animate && "softFloat 6s ease-in-out infinite"};
  animation-delay: ${(props) => props.$delay || "0s"};

  @keyframes softFloat {
    0%,
    100% {
      transform: rotate(${(props) => props.$rotate || "0deg"}) translateY(0px);
    }
    50% {
      transform: rotate(${(props) => props.$rotate || "0deg"}) translateY(-20px);
    }
  }
`;

const Hill = styled.div`
  position: absolute;
  bottom: 0;
  width: ${(props) => props.$width || "100%"};
  height: ${(props) => props.$height || "12vh"};
  background: ${(props) =>
    props.$gradient ||
    "linear-gradient(to bottom, rgba(167, 243, 208, 0.4) 0%, rgba(134, 239, 172, 0.5) 100%)"};
  border-radius: 100% 100% 0 0;
  left: ${(props) => props.$left || "0"};
  right: ${(props) => props.$right || "auto"};
  transform: ${(props) => props.$transform || "scaleX(1.1) translateY(2px)"};
  pointer-events: none;
  z-index: ${(props) => props.$zIndex || "1"};
`;

const TopNavBar = styled.div`
  grid-area: topNav;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  position: fixed;
  left: clamp(1.5rem, 2.5vw, 3rem);
  top: clamp(1.5rem, 2.5vw, 3rem);
  z-index: 20;
  cursor: pointer;

  .logo-icon {
    font-size: clamp(3rem, 4vw, 5rem);
    color: #ec4899;
    opacity: 0.5;
    filter: drop-shadow(0 4px 8px rgba(236, 72, 153, 0.3));
    animation: logoFloat 3s ease-in-out infinite;
  }

  @keyframes logoFloat {
    0%,
    100% {
      transform: translateY(0) rotate(-5deg);
    }
    50% {
      transform: translateY(-8px) rotate(5deg);
    }
  }
`;

const TopNavGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-template-rows: 100%;
  grid-template-areas: ". login lang";
  column-gap: clamp(0.5rem, 1vw, 1.5rem);
  padding-right: clamp(1rem, 2vw, 3rem);
`;

const LogIn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-area: login;
`;

const LangSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-area: lang;
  position: relative;
  font-family: "Nunito", "Quicksand", sans-serif;
`;

const DefaultLang = styled.button`
  display: flex;
  align-items: center;
  gap: clamp(0.4rem, 0.6vw, 0.75rem);
  background-color: #fff;
  padding: clamp(0.5rem, 0.8vw, 0.75rem) clamp(1rem, 1.5vw, 1.7rem);
  border-radius: clamp(1rem, 1.2vw, 1.5rem);
  font-size: clamp(1rem, 1.2vw, 1.3rem);
  font-weight: 700;
  color: #0ea5e9;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  border-bottom: clamp(2px, 0.3vw, 4px) solid #e0f2fe;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:active {
    transform: translateY(1px);
    border-bottom-width: 0;
  }

  .lang-icon {
    font-size: clamp(0.7rem, 0.9vw, 1rem);
    transition: transform 0.2s ease;
  }

  .chevron-icon {
    width: clamp(1rem, 1.1vw, 1.25rem);
    height: clamp(1rem, 1.1vw, 1.25rem);
    opacity: 0.4;
    transition: transform 0.3s ease;
  }
`;

const DropDown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + clamp(0.5rem, 0.7vw, 0.75rem));
  width: clamp(10rem, 11vw, 12rem);
  background-color: #fff;
  border-radius: clamp(1.2rem, 1.6vw, 1.875rem);
  box-shadow:
    0 20px 25px -5px rgba(14, 165, 233, 0.3),
    0 10px 10px -5px rgba(14, 165, 233, 0.2);
  border: clamp(2px, 0.3vw, 4px) solid #f0f9ff;
  padding: clamp(0.5rem, 0.7vw, 0.75rem) 0;
  z-index: 60;
  opacity: ${(props) => (props.$isVisible ? "1" : "0")};
  visibility: ${(props) => (props.$isVisible ? "visible" : "hidden")};
  transform: ${(props) => (props.$isVisible ? "scale(1)" : "scale(0.95)")};
  transform-origin: top right;
  transition: all 0.2s ease;
`;

const LangUl = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LangLi = styled.button`
  width: 100%;
  text-align: left;
  padding: clamp(0.6rem, 0.8vw, 0.875rem) clamp(1rem, 1.3vw, 1.5rem);
  font-size: clamp(0.85rem, 0.95vw, 1rem);
  font-weight: 700;
  color: #475569;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 0.7vw, 0.75rem);
  transition: background-color 0.2s ease;
  font-family: "Nunito", "Quicksand", sans-serif;

  &:hover {
    background-color: #f0f9ff;
  }

  .flag-icon {
    font-size: clamp(0.8rem, 0.9vw, 1rem);
  }
`;

const LangDivider = styled.div`
  margin: clamp(0.15rem, 0.2vw, 0.25rem) clamp(0.7rem, 0.9vw, 1rem);
  border-top: clamp(1px, 0.15vw, 2px) dashed #f8fafc;
`;

const Content = styled.div`
  grid-area: content;
  position: relative;
`;

const ContentLeft = styled.div`
  position: absolute;
  left: 8%;
  top: 40%;
  transform: translateY(-50%);
  width: 40%;
  z-index: 10;
`;

const Badge = styled.div`
  display: inline-block;
  padding: 0.4em 1.2em;
  background-color: #e0e7ff;
  color: #4f46e5;
  border-radius: 2em;
  font-family: "Nunito", sans-serif;
  font-weight: 700;
  font-size: clamp(1rem, 1.5vw, 1.8rem);
  margin-bottom: 1.5em;
  animation: bounce 2s infinite;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  /* @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  } */
`;

const MainTitle = styled.h1`
  font-family: "Fredoka", "Nunito", sans-serif;
  font-size: clamp(2rem, 4vw, 5rem);
  font-weight: 900;
  color: #1e1b4b;
  line-height: 1.2;
  margin: 0 0 0.8em 0;

  .highlight {
    color: #ec4899;
    text-decoration: underline;
    text-decoration-color: #fbbf24;
    text-decoration-style: wavy;
    text-decoration-thickness: 0.15em;
  }
`;

const MainDescription = styled.p`
  font-family: "Nunito", sans-serif;
  font-size: clamp(1rem, 1.8vw, 2rem);
  font-weight: 500;
  color: rgba(30, 27, 75, 0.7);
  line-height: 1.6;
  margin: 0;
`;

const ContentRight = styled.div`
  position: absolute;
  right: 5%;
  top: 40%;
  transform: translateY(-50%);
  width: 40%;
  z-index: 10;
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  filter: drop-shadow(0 1vw 2vw rgba(0, 0, 0, 0.2));
  opacity: ${(props) => (props.$loaded ? "1" : "0")};
  transition: opacity 0.3s ease-in;
`;

const PlayfulLoginButton = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: "Nunito", "Quicksand", sans-serif;

  .button-shadow {
    position: absolute;
    inset: 0;
    background-color: #059669;
    border-radius: clamp(1rem, 1.2vw, 1.5rem);
    transform: translateY(clamp(0.25rem, 0.3vw, 0.375rem));
  }

  .button-main {
    position: relative;
    background-color: #34d399;
    color: white;
    padding: clamp(0.6rem, 0.8vw, 0.875rem) clamp(1rem, 1.2vw, 1.3rem);
    border-radius: clamp(1rem, 1.2vw, 1.5rem);
    font-weight: 700;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: clamp(0.3rem, 0.4vw, 0.5rem);
  }

  &:hover .button-main {
    background-color: #6ee7b7;
  }

  &:hover .key-icon {
    transform: rotate(12deg);
  }

  &:active .button-main {
    transform: translateY(clamp(0.25rem, 0.3vw, 0.375rem));
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:disabled .button-main,
  &:disabled:hover .button-main,
  &:disabled:active .button-main {
    background-color: #34d399;
    transform: none;
  }

  &:disabled:hover .key-icon {
    transform: none;
  }

  .key-icon {
    font-size: clamp(1rem, 1.1vw, 1.25rem);
    transition: transform 0.2s ease;
  }

  .button-text {
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: clamp(0.9rem, 1.1vw, 1.2rem);
  }

  .button-text-cn {
    opacity: 0.7;
    margin-left: clamp(0.15rem, 0.2vw, 0.25rem);
  }
`;

const LanguageCode = styled.span`
  font-size: clamp(0.6rem, 0.65vw, 0.7rem);
  font-weight: 900;
  color: #0284c7;
  background-color: #e0f2fe;
  padding: clamp(0.2rem, 0.23vw, 0.25rem) clamp(0.4rem, 0.45vw, 0.5rem);
  border-radius: clamp(0.4rem, 0.45vw, 0.5rem);
`;

const TabletLandingPage = () => {
  const signout = useSignout();
  const user = useSelector((state) => state.auth.user);
  const signin = useMyBestUserSignin();
  const ssoAutoLoginState = useSSOAutoLoginState();

  // "success" at MOUNT means the silent attempt already carried us off to
  // /mybestuser and the exchange there failed (MyBestUserLogin's error
  // dialog sends the user back to "/"): the verdict is stale, and the only
  // sign-in left is the dialog. While this page is mounted, "success" can
  // only arrive as a change, right before the navigation away.
  const cameBackAfterSilentAttempt = useRef(ssoAutoLoginState === "success");
  const ssoVerdict = cameBackAfterSilentAttempt.current
    ? "none"
    : ssoAutoLoginState;
  const isSilentSignInPending = !user && ssoVerdict === "pending";

  const openSignInDialog = () =>
    signin(
      `${MYBESTUSER}/authorize` +
        qs.stringify(
          {
            client_id: CLIENTID,
            response_type: "code",
            redirect_uri: window.location.origin + "/mybestuser",
            state: "state",
            scope: "api.mybestta.com api.mybestuser.com",
            isTablet: "true"
          },
          { addQueryPrefix: true }
        )
    );

  const { language, changeLanguage } = useLanguage();
  const [isShowLangList, setIsShowLangList] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const ToggleLangList = () => {
    setIsShowLangList((preState) => !preState);
  };

  const handleChangeLang = (value) => {
    changeLanguage(value);
    setIsShowLangList(false);
  };

  useEffect(() => {
    if (window.location.href.indexOf(".cn") !== -1) {
      changeLanguage("cn");
    } else {
      changeLanguage("tw");
    }

    // Preload the image
    const img = new Image();
    img.src = threeChildrenImg;
    img.onload = () => setImageLoaded(true);
  }, []);

  // Auto-trigger sign-in dialog if user is not logged in — but only once the
  // silent SSO auto-login has said it is not signing anyone in. The dialog
  // signs in through the same mybestuser cookie the silent attempt uses, so
  // opening it while that attempt is in flight ran two sign-ins at once;
  // the second consumed a stale state and forced a reload onto the role
  // home instead of the page the tablet had asked for. "pending" means keep
  // waiting; "success" means the silent attempt is navigating us away.
  useEffect(() => {
    if (user) return;
    if (ssoVerdict !== "none") return;

    openSignInDialog();
    // Re-run only when the verdict changes: `user` flipping later means the
    // sign-in finished, at which point there is nothing to open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoVerdict]);

  return (
    <MainBGContent>
      {/* Soft Watercolor Blobs */}
      <BackgroundBlob
        $top="-10%"
        $left="-5%"
        $width="40vw"
        $height="40vw"
        $color="#dbeafe"
        $opacity="0.3"
        $blur="100px"
      />
      <BackgroundBlob
        $bottom="-10%"
        $right="-5%"
        $width="50vw"
        $height="50vw"
        $color="#fff1f2"
        $opacity="0.4"
        $blur="120px"
      />
      <BackgroundBlob
        $top="20%"
        $right="10%"
        $width="30vw"
        $height="30vw"
        $color="#fef3c7"
        $opacity="0.3"
        $blur="80px"
      />
      <BackgroundBlob
        $top="50%"
        $left="20%"
        $width="20vw"
        $height="20vw"
        $color="#d1fae5"
        $opacity="0.4"
        $blur="60px"
      />

      {/* Cute Animal Icons */}
      <FloatingIcon
        $top="15%"
        $left="13%"
        $size="4rem"
        $color="#f59e0b"
        $opacity="0.3"
        $rotate="-15deg"
        $animate
        $delay="0s"
      >
        <FontAwesomeIcon icon={faCat} />
      </FloatingIcon>
      <FloatingIcon
        $top="35%"
        $left="85%"
        $size="3.5rem"
        $color="#ec4899"
        $opacity="0.3"
        $rotate="12deg"
        $animate
        $delay="1s"
      >
        <FontAwesomeIcon icon={faDog} />
      </FloatingIcon>
      <FloatingIcon
        $top="70%"
        $left="15%"
        $size="4rem"
        $color="#3b82f6"
        $opacity="0.3"
        $rotate="8deg"
        $animate
        $delay="2s"
      >
        <FontAwesomeIcon icon={faFish} />
      </FloatingIcon>
      <FloatingIcon
        $top="12%"
        $left="80%"
        $size="3rem"
        $color="#8b5cf6"
        $opacity="0.3"
        $rotate="-10deg"
        $animate
        $delay="1.5s"
      >
        <FontAwesomeIcon icon={faDove} />
      </FloatingIcon>
      <FloatingIcon
        $top="55%"
        $left="75%"
        $size="3.5rem"
        $color="#10b981"
        $opacity="0.3"
        $rotate="15deg"
        $animate
        $delay="0.5s"
      >
        <FontAwesomeIcon icon={faCrow} />
      </FloatingIcon>

      {/* Reduced Letters and Numbers */}
      <FloatingIcon
        $top="5%"
        $left="35%"
        $size="4rem"
        $color="#f59e0b"
        $opacity="0.3"
        $rotate="20deg"
        $animate
        $delay="2.5s"
      >
        <FontAwesomeIcon icon={faA} />
      </FloatingIcon>
      <FloatingIcon
        $top="70%"
        $left="60%"
        $size="3.5rem"
        $color="#ec4899"
        $opacity="0.3"
        $rotate="-15deg"
        $animate
        $delay="3s"
      >
        <FontAwesomeIcon icon={faB} />
      </FloatingIcon>
      <FloatingIcon
        $top="40%"
        $left="45%"
        $size="4rem"
        $color="#3b82f6"
        $opacity="0.3"
        $rotate="10deg"
        $animate
        $delay="1.2s"
      >
        <FontAwesomeIcon icon={fa1} />
      </FloatingIcon>
      <FloatingIcon
        $top="70%"
        $left="40%"
        $size="3.5rem"
        $color="#10b981"
        $opacity="0.3"
        $rotate="-8deg"
        $animate
        $delay="2.8s"
      >
        <FontAwesomeIcon icon={fa2} />
      </FloatingIcon>
      <FloatingIcon
        $top="10%"
        $left="50%"
        $size="4rem"
        $color="#8b5cf6"
        $opacity="0.3"
        $rotate="18deg"
        $animate
        $delay="0.8s"
      >
        <FontAwesomeIcon icon={fa3} />
      </FloatingIcon>

      {/* Decorative Hills */}
      <Hill
        $gradient="linear-gradient(to bottom, rgba(167, 243, 208, 1) 0%, rgba(134, 239, 172, 1) 50%, rgba(110, 231, 183, 1) 100%)"
        $height="15vh"
        $transform="scaleX(1.1) translateY(2px)"
        $zIndex="2"
      />
      <Hill
        $width="55%"
        $height="18vh"
        $gradient="linear-gradient(to bottom, rgba(147, 197, 253, 0.4) 0%, rgba(96, 165, 250, 0.5) 50%, rgba(59, 130, 246, 0.6) 100%)"
        $right="-10%"
        $left="auto"
        $transform="rotate(1deg)"
        $zIndex="1"
      />

      <Logo>
        <FontAwesomeIcon icon={faGraduationCap} className="logo-icon" />
      </Logo>

      <TopNavBar>
        <TopNavGrid>
          <LogIn>
            {user?.roles?.some((role) =>
              ACCESS_LEVELS[ADMINS].includes(role)
            ) ? (
              <ButtonLink as={Link} to="/admin" />
            ) : user?.roles?.some((role) =>
                ACCESS_LEVELS[ALL].includes(role)
              ) ? (
              <ButtonLink as={Link} to="/teacher" />
            ) : user?.roles?.some((role) =>
                ACCESS_LEVELS[STUDENT].includes(role)
              ) ? (
              <ButtonLink as={Link} to="/student" />
            ) : null}

            {user ? (
              <PlayfulLoginButton
                as={Link}
                to="/"
                onClick={(event) => {
                  // On the tablet the sign-in app owns sign-out: it shows
                  // its account card and the user decides there
                  if (openTabletAuthForLogout()) {
                    event.preventDefault();
                    return;
                  }
                  signout();
                }}
              >
                <div className="button-shadow" />
                <div className="button-main">
                  <span className="key-icon">🔑</span>
                  <span className="button-text">
                    Log Out <span className="button-text-cn">登出</span>
                  </span>
                </div>
              </PlayfulLoginButton>
            ) : (
              // Held while the silent SSO attempt is still out: a tap here
              // would start the second sign-in the auto-open above waits to
              // avoid. The label says why the button is not responding.
              <PlayfulLoginButton
                onClick={openSignInDialog}
                disabled={isSilentSignInPending}
              >
                <div className="button-shadow" />
                <div className="button-main">
                  <span className="key-icon">🔑</span>
                  <span className="button-text">
                    {isSilentSignInPending ? (
                      <>
                        Signing In{" "}
                        <span className="button-text-cn">登入中…</span>
                      </>
                    ) : (
                      <>
                        Log In <span className="button-text-cn">登入</span>
                      </>
                    )}
                  </span>
                </div>
              </PlayfulLoginButton>
            )}
          </LogIn>

          <LangSelect>
            <DefaultLang onClick={ToggleLangList}>
              <LanguageCode className="lang-icon">
                {language === "cn" ? "CN" : language === "tw" ? "TW" : "EN"}
              </LanguageCode>
              <span>
                {language === "cn"
                  ? "简体"
                  : language === "tw"
                    ? "繁體"
                    : "English"}
              </span>
              <svg
                className="chevron-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="4"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </DefaultLang>
            <DropDown $isVisible={isShowLangList}>
              <LangUl>
                <LangLi
                  onClick={() => {
                    handleChangeLang("cn");
                  }}
                >
                  <LanguageCode className="flag-icon">CN</LanguageCode>
                  <span>简体中文</span>
                </LangLi>
                <LangDivider />
                <LangLi
                  onClick={() => {
                    handleChangeLang("tw");
                  }}
                >
                  <LanguageCode className="flag-icon">TW</LanguageCode>
                  <span>繁體中文</span>
                </LangLi>
                <LangDivider />
                <LangLi
                  onClick={() => {
                    handleChangeLang("en");
                  }}
                >
                  <LanguageCode className="flag-icon">EN</LanguageCode>
                  <span>English</span>
                </LangLi>
              </LangUl>
            </DropDown>
          </LangSelect>
        </TopNavGrid>
      </TopNavBar>
      <Content>
        <ContentLeft>
          <Badge>
            {language === "cn"
              ? "伴学Pro 🍭"
              : language === "tw"
                ? "伴學Pro 🍭"
                : " LearnMate Pro 🍭"}
          </Badge>
          <MainTitle>
            {language === "cn" ? (
              <>
                玩乐、学习
                <br />
                <span className="highlight">一起成长！</span>
              </>
            ) : language === "tw" ? (
              <>
                玩樂、學習
                <br />
                <span className="highlight">一起成長！</span>
              </>
            ) : (
              <>
                Master English
                <br />
                <span className="highlight">Through Play!</span>
              </>
            )}
          </MainTitle>
          <MainDescription>
            {language === "cn"
              ? "互动课程、可爱角色和有趣游戏，让每个孩子都爱上学习。"
              : language === "tw"
                ? "互動課程、可愛角色和有趣遊戲，讓每個孩子都愛上學習。"
                : "Interactive lessons, cute characters, and fun games designed to make every child fall in love with learning."}
          </MainDescription>
        </ContentLeft>
        <ContentRight>
          <HeroImage
            src={threeChildrenImg}
            alt="Three children learning"
            $loaded={imageLoaded}
          />
        </ContentRight>
      </Content>
    </MainBGContent>
  );
};

export default TabletLandingPage;
